import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import { FileDetailsResponse, MediaLibrarySearchFileResponse } from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface ExtensionPreset {
  id: string
  name: string
  description: string
  config: Record<string, any>
}

type ExtensionStatusValue = 'success' | 'pending' | 'failed'

interface ExtensionStatus {
  'google-auto-tagging'?: ExtensionStatusValue
  'aws-auto-tagging'?: ExtensionStatusValue
  'remove-bg'?: ExtensionStatusValue
  'ai-auto-description'?: ExtensionStatusValue
  'ai-tasks'?: ExtensionStatusValue
  [key: string]: ExtensionStatusValue | undefined // For saved-extension IDs
}

interface UpdatedFile {
  fileId: string
  extensionStatus?: ExtensionStatus
  [key: string]: any
}

interface ApplyExtensionResult {
  successfullyUpdatedFiles?: UpdatedFile[]
  errors?: Array<{ fileId: string; message: string }>
  message?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  fileDetails?: FileDetailsResponse
  setFileDetails?: (file: FileDetailsResponse) => void
}

const ApplySavedExtensionModal: React.FC<Props> = ({ isOpen, onClose, fileDetails, setFileDetails }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const [{ selectedFiles: contextSelectedFiles }, dispatch] = useContext(MLContext)
  const [selectedExtensionIds, setSelectedExtensionIds] = useState<string[]>([])
  const [formError, setFormError] = useState<string>('')
  const [isApplying, setIsApplying] = useState(false)

  const selectedFiles = useMemo(() => {
    if (fileDetails) {
      return [fileDetails as unknown as MediaLibrarySearchFileResponse]
    }
    return contextSelectedFiles
  }, [fileDetails, contextSelectedFiles])

  const {
    data: extensionsData,
    loading: extensionsLoading,
    error: extensionsError,
    get: fetchExtensions,
  } = useFetch<ExtensionPreset[]>('/api/v1/saved-extensions', {
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    if (isOpen) {
      void fetchExtensions()
      setSelectedExtensionIds([])
      setFormError('')
    }
  }, [isOpen])

  const handleToggleExtension = (extensionId: string) => {
    setSelectedExtensionIds(prev =>
      prev.includes(extensionId)
        ? prev.filter(id => id !== extensionId)
        : [...prev, extensionId],
    )
  }

  const handleApply = async () => {
    if (selectedExtensionIds.length === 0) {
      setFormError(
        t(
          'select_at_least_one_extension',
          'Please select at least one extension.',
        ),
      )
      return
    }

    setFormError('')
    setIsApplying(true)

    const extensions = selectedExtensionIds.map(id => ({
      name: 'saved-extension',
      id,
    }))

    const result: ApplyExtensionResult = await request.post(
      `/api/media-library/${user?.clientNumber}/files/bulk/apply-extension`,
      {
        fileIds: selectedFiles.map(file => file.fileId),
        extensions,
      },
    )

    setIsApplying(false)

    if (request.error) {
      // Show error in Alert
      setFormError(
        (result as any)?.message ||
          t(
            'error_applying_extensions_try_again',
            'Error applying extensions, please try again after sometime.',
          ),
      )
      return
    }

    const { successfullyUpdatedFiles, errors } = result

    // Analyze extension status across all files
    const statusCounts = {
      success: 0,
      pending: 0,
      failed: 0,
    }

    successfullyUpdatedFiles?.forEach(file => {
      if (file.extensionStatus) {
        Object.values(file.extensionStatus).forEach(status => {
          if (status === 'success') statusCounts.success++
          else if (status === 'pending') statusCounts.pending++
          else if (status === 'failed') statusCounts.failed++
        })
      }
    })

    const totalFiles = selectedFiles.length
    const successCount = successfullyUpdatedFiles?.length || 0
    const errorCount = errors?.length || 0

    // Check if there are any failures (file-level or extension-level)
    const hasFailures = errorCount > 0 || statusCounts.failed > 0

    // Handle all files failed
    if (errorCount === totalFiles || (!successfullyUpdatedFiles || successfullyUpdatedFiles.length === 0)) {
      setFormError(
        t(
          'all_files_failed',
          'Failed to apply extensions to all files. Please try again.',
        ),
      )
      return
    }

    // Handle extension status errors - all extensions failed
    if (statusCounts.failed > 0 && statusCounts.pending === 0 && statusCounts.success === 0) {
      setFormError(
        t('extensions_failed', 'Extension processing failed for all files. Please try again.'),
      )
      return
    }

    // Update files in store
    dispatch({
      type: MediaLibraryActions.UPDATE_FILES,
      payload: successfullyUpdatedFiles as any,
    })

    // Update file detail if in detail view
    if (fileDetails && setFileDetails && successfullyUpdatedFiles && successfullyUpdatedFiles.length > 0) {
      const updatedFile = successfullyUpdatedFiles.find(f => f.fileId === fileDetails.fileId)
      if (updatedFile) {
        setFileDetails(updatedFile as unknown as FileDetailsResponse)
      }
    }

    // Build detailed message parts
    const messageParts: string[] = []
    
    if (successCount > 0) {
      messageParts.push(`${successCount} file(s) updated`)
    }
    
    if (statusCounts.success > 0) {
      messageParts.push(`${statusCounts.success} extension(s) succeeded`)
    }
    
    if (statusCounts.pending > 0) {
      messageParts.push(`${statusCounts.pending} extension(s) pending`)
    }
    
    if (statusCounts.failed > 0) {
      messageParts.push(`${statusCounts.failed} extension(s) failed`)
    }
    
    if (errorCount > 0) {
      messageParts.push(`${errorCount} file(s) failed`)
    }

    const detailedMessage = messageParts.join(', ') + '.'

    // If there are any failures, show in Alert (modal stays open)
    if (hasFailures) {
      setFormError(detailedMessage)
      return
    }

    // No failures - show success/info toast with full details and close modal
    if (statusCounts.pending > 0) {
      // Has pending extensions - show info toast with all details
      toast({
        description: `${detailedMessage} Extensions will be processed shortly.`,
        status: 'info',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      // All successful - show success toast with all details
      toast({
        description: detailedMessage,
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }

    onClose()
  }

  const extensions = Array.isArray(extensionsData) ? extensionsData : []

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('apply_saved_extensions', 'Apply saved extensions')}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {formError ? (
            <Alert status="error" mb="1em">
              <AlertIcon />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          {extensionsLoading ? (
            <Box display="flex" justifyContent="center" padding={8}>
              <Spinner size="lg" />
            </Box>
          ) : extensionsError ? (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>
                {t(
                  'error_loading_extensions',
                  'Error loading saved extensions. Please try again.',
                )}
              </AlertDescription>
            </Alert>
          ) : extensions.length === 0 ? (
            <Text color={colors.appBattleshipGrey}>
              {t(
                'no_saved_extensions_available',
                'No saved extensions available. Create one in Settings first.',
              )}
            </Text>
          ) : (
            <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
              <Text fontSize="sm" color={colors.appBattleshipGrey} mb={2}>
                <Trans
                  i18nKey="saved_extensions_available"
                  count={extensions.length}
                >
                  Saved extensions ({{ count: extensions.length }} available)
                </Trans>
              </Text>
              {extensions.map(extension => (
                <Checkbox
                  key={extension.id}
                  isChecked={selectedExtensionIds.includes(extension.id)}
                  onChange={() => handleToggleExtension(extension.id)}
                >
                  <Text fontWeight="medium">{extension.name}</Text>
                </Checkbox>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button
            variant="outline"
            onClick={() => {
              onClose()
              setFormError('')
            }}
            isDisabled={isApplying}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appBlue"
            onClick={handleApply}
            isLoading={isApplying}
            loadingText={t('applying', 'Applying...')}
            isDisabled={
              extensions.length === 0 ||
              selectedExtensionIds.length === 0 ||
              isApplying
            }
          >
            {t('apply_selected', 'Apply selected')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ApplySavedExtensionModal
